import { Autocomplete } from '@mui/material';
import debounce from 'shell/utils/debounce';
import { memo, useCallback, useMemo } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { ItemVM } from 'shell/view-models/item';
import { Operation } from 'shell/view-models/operation';
import { paginationListToQueryString } from 'system/utils/list-object-to-query-string';
import { OperationObject } from 'system/utils/operation-object-to-query-string';

interface AutocompleteListaSediProps {
  disabled?: boolean;
  required?: boolean;
  value: ItemVM | null;
  onChange: (value?: ItemVM) => void;
  error?: boolean;
  isSmallSize?: boolean;
  label?: string;
}

const AutocompleteListaSediComponent = ({
  disabled,
  required,
  onChange,
  value,
  error,
  label,
}: AutocompleteListaSediProps): React.ReactElement => {
  const [sedi, getSedi] = useHttpFetch<HttpResponseList<string>, Array<ItemVM>>(
    `${__PRACTICE_API_URL__}/pratiche/ufficio-serie-locazioni/codiciUfficio`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        abortable: true,
        method: 'GET',
        loading: false,
      },
      transformer: (response) =>
        response.data.payload.list.map((item) => ({
          id: item,
          value: item,
        })) as Array<ItemVM>,
    }
  );

  const sediData: Array<ItemVM> = useMemo(() => {
    return (sedi?.data || []) as Array<ItemVM>;
  }, [sedi?.data]);

  const defaultProps = useMemo(() => {
    return {
      options: (sedi.data as Array<ItemVM>) || [],
      getOptionLabel: (option: ItemVM) => option.value,
    };
  }, [sedi.data]);

  const handleSearch = debounce<string>(
    useCallback(
      (searchValue) => {
        if (sedi.abort) {
          sedi.abort();
        }

        const transformedFilters: OperationObject = {
          codUfficio: { value: searchValue || '', operation: Operation.LIKE },
        };

        const queryString = `${paginationListToQueryString({
          filters: transformedFilters,
          orders: {},
        })}`;

        getSedi({
          params: {},
          paramsSerializer: () => queryString,
        });
      },
      [getSedi, sedi.abort]
    )
  );

  return (
    <Autocomplete
      {...defaultProps}
      id={`${label}-id`}
      value={value}
      noOptionsText="Nessun elemento"
      loadingText="Caricamento..."
      onChange={(event, element) => {
        event.stopPropagation();
        onChange(sediData.find((item) => item.id === element?.id));
      }}
      disabled={disabled}
      onInputChange={(event, changeValue, reason) => {
        if (!changeValue) {
          return;
        }
        if (reason === 'input') {
          handleSearch(changeValue);
        }
      }}
      clearOnBlur={false}
      loading={sedi.isLoading}
      renderInput={(params) => (
        <PmiTextField
          {...params}
          label={required ? `${label} *` : label}
          variant="outlined"
          error={error}
        />
      )}
    />
  );
};

AutocompleteListaSediComponent.displayName = 'Autocomplete list sedi component';

const AutocompleteListaSedi = memo(AutocompleteListaSediComponent);

export default AutocompleteListaSedi as typeof AutocompleteListaSediComponent;
